// apps/pwa-pos/src/lib/customers.ts
// Sprint 37 Fase 2 — Cache local de clientes para el selector de fiado.
// Mismo patrón que catalog.ts: lectura desde Supabase PostgREST + cache en
// localStorage con TTL de 6h. El POS necesita los clientes offline porque el
// checkout de crédito requiere validar `limite_credito` y `saldo_actual`.

import type { SupabaseClient } from '@supabase/supabase-js';
import { connector } from './auth';

export interface Customer {
  id: string;
  organization_id: string;
  codigo?: string | null;
  nombre: string;
  tipo_documento?: string | null;
  numero_documento?: string | null;
  limite_credito: number;
  dias_credito: number;
  saldo_actual: number;
  telefono?: string | null;
  email?: string | null;
}

interface CustomersSnapshot {
  items: Customer[];
  refreshedAt: string;
  orgId: string | null;
}

const CACHE_KEY_PREFIX = 'mibodega.customers.';
const TTL_MS = 6 * 60 * 60 * 1000;

function cacheKey(orgId: string | null): string {
  return `${CACHE_KEY_PREFIX}${orgId ?? '_'}`;
}

export function readCustomers(orgId: string | null): CustomersSnapshot | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(cacheKey(orgId));
    if (!raw) return null;
    const snap = JSON.parse(raw) as CustomersSnapshot;
    if (!snap.items || !Array.isArray(snap.items)) return null;
    return snap;
  } catch {
    return null;
  }
}

export function writeCustomers(orgId: string | null, items: Customer[]): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const snap: CustomersSnapshot = {
      items,
      refreshedAt: new Date().toISOString(),
      orgId,
    };
    localStorage.setItem(cacheKey(orgId), JSON.stringify(snap));
  } catch (err) {
    console.warn('[customers] No se pudo persistir el cache:', err);
  }
}

/** Test hook — same pattern as catalog. */
let clientOverride: SupabaseClient | null | undefined;
export function __setCustomersClientForTests(client: SupabaseClient | null | undefined): void {
  clientOverride = client;
}

function getClient(): SupabaseClient | null {
  if (clientOverride !== undefined) return clientOverride;
  // Un solo client con la sesión del login (evita race de JWT en createClient extra).
  return connector.client ?? null;
}

/**
 * Refresca los clientes desde Supabase. Si falla, mantiene el cache anterior.
 * Nunca pisa un cache con items con un [] “exitoso” (RLS/anon).
 */
export async function refreshCustomers(orgId: string | null): Promise<Customer[]> {
  const previous = readCustomers(orgId)?.items ?? [];
  const client = getClient();
  if (!client) {
    return previous;
  }

  try {
    let session = (await client.auth.getSession()).data.session;
    if (!session?.access_token && typeof client.auth.refreshSession === 'function') {
      session = (await client.auth.refreshSession()).data.session;
    }
    if (!session?.access_token) {
      console.warn('[customers] sin sesión JWT; se conserva cache local');
      return previous;
    }

    const { data, error } = await client
      .from('clientes')
      .select(
        'id, organization_id, codigo, nombre, tipo_documento, numero_documento, limite_credito, dias_credito, saldo_actual, telefono, email',
      )
      .eq('organization_id', orgId ?? '')
      .eq('activo', true)
      .order('nombre', { ascending: true })
      .limit(2000);

    if (error) {
      console.warn('[customers] error al refrescar:', error.message);
      return previous;
    }

    const items: Customer[] = (data ?? []).map((row) => ({
      id: row.id as string,
      organization_id: row.organization_id as string,
      codigo: (row.codigo as string | null) ?? null,
      nombre: row.nombre as string,
      tipo_documento: (row.tipo_documento as string | null) ?? null,
      numero_documento: (row.numero_documento as string | null) ?? null,
      limite_credito: Number(row.limite_credito ?? 0),
      dias_credito: Number(row.dias_credito ?? 0),
      saldo_actual: Number(row.saldo_actual ?? 0),
      telefono: (row.telefono as string | null) ?? null,
      email: (row.email as string | null) ?? null,
    }));

    if (items.length === 0 && previous.length > 0) {
      console.warn(`[customers] remoto vacío con ${previous.length} locales — se conserva cache`);
      return previous;
    }

    writeCustomers(orgId, items);
    return items;
  } catch (err) {
    console.warn('[customers] refresh fallo, usando cache:', err);
    return previous;
  }
}

/**
 * Lectura principal desde la UI: intenta traer fresco y cae al cache.
 */
export async function loadCustomers(orgId: string | null): Promise<Customer[]> {
  const items = await refreshCustomers(orgId);
  if (items.length === 0) {
    const cached = readCustomers(orgId);
    return cached?.items ?? [];
  }
  return items;
}

/** Metadatos útiles para mostrar en UI. */
export function customersStatus(orgId: string | null): {
  count: number;
  refreshedAt: string | null;
  isStale: boolean;
} {
  const cached = readCustomers(orgId);
  if (!cached) return { count: 0, refreshedAt: null, isStale: true };
  const age = Date.now() - new Date(cached.refreshedAt).getTime();
  return {
    count: cached.items.length,
    refreshedAt: cached.refreshedAt,
    isStale: age > TTL_MS,
  };
}

/** Ajusta el saldo local. Devuelve el saldo nuevo o null si no había cache. */
export function adjustCustomerSaldo(
  orgId: string | null,
  clienteId: string,
  delta: number,
): number | null {
  const snap = readCustomers(orgId);
  if (!snap) return null;
  let nextSaldo: number | null = null;
  const items = snap.items.map((c) => {
    if (c.id !== clienteId) return c;
    const saldo = Number(c.saldo_actual) + delta;
    nextSaldo = Number.isFinite(saldo) ? Math.round(saldo * 100) / 100 : c.saldo_actual;
    return { ...c, saldo_actual: nextSaldo };
  });
  if (nextSaldo === null) return null;
  writeCustomers(orgId, items);
  return nextSaldo;
}

/** ¿El cliente admite que se le fíe este monto? */
export function clienteAdmiteFiar(c: Customer, montoAdicional: number): boolean {
  if (c.limite_credito <= 0) return false;
  return c.saldo_actual + montoAdicional <= c.limite_credito + 0.01;
}

/** Crédito disponible restante del cliente. */
export function creditoDisponible(c: Customer): number {
  return Math.max(0, c.limite_credito - c.saldo_actual);
}
